/* eslint-disable jsdoc/check-param-names, jsdoc/require-param */
import {Dispatch, useEffect} from 'react'
import {useDispatch} from 'react-redux'
import {lensPath, set} from 'ramda'

import {useBrandCapabilities} from '../../_web/brand/hooks/useBrandCapabilities'
import {createBroadcastChannel} from '../../_web/utils/broadcastChannel'
import {safelyGetStorageItem} from '../../_web/utils/persistence'
import AnalyticsUtils from '../../analytics/utils'
import {loadFullStory} from '../../analytics/utils/FullStory'
import {authenticationErrored} from '../business/authenticationErrorEvent'
import {userHasJustAuthenticated} from '../modules/actions'
import {getConnection} from '../modules/selectors'
import {logOut, setIsLoading, setUser} from '../modules/slice'
import {
  AUTHENTICATION_SUCCESS_STORAGE_KEY,
  getCurrentUserService
} from '../services'
import {AuthBroadcastChannelMessages} from '../types/Auth'
import {User} from '../types/Cognito'
import {isCognitoRoute} from '../utils/url'

interface FetchUserProps {
  publicUrl?: string | null
}

/**
 * Options for the `updateUser` function.
 * @param publicUrl - Public URL for FullStory integration
 * @param dispatch - Redux dispatch function
 * @param skipLoadingState - Whether to skip setting loading state
 */
type UpdateUserProps = {
  publicUrl?: FetchUserProps['publicUrl']
  dispatch: Dispatch<unknown>
  skipLoadingState?: boolean
}

/**
 * Updates the user state and handles authentication-related side effects.
 * @param props - The user update options
 * @returns The authenticated user object if successful
 */
export const updateUser = async ({
  publicUrl,
  dispatch,
  skipLoadingState = false
}: UpdateUserProps) => {
  if (!skipLoadingState) dispatch(setIsLoading(true))

  try {
    const {user, userPoolId} = await getCurrentUserService()
    const isUserAuthenticated = Boolean(user?.id)

    AnalyticsUtils.resetAnalytics(user?.attributes?.sub)

    if (isUserAuthenticated) {
      const payload = {
        user,
        isAuthenticated: true,
        shouldSeeOffersUnlocked: true,
        userPoolId
      }

      const userWithAuthenticationMethod = set(
        lensPath(['user', 'attributes', 'authenticationMethod']),
        getConnection(user as User),
        payload
      )

      dispatch(setUser(userWithAuthenticationMethod))

      if (publicUrl) loadFullStory(publicUrl)

      dispatch(setIsLoading(false))
      return user
    } else {
      dispatch(logOut())
    }
  } catch (error) {
    dispatch(
      authenticationErrored({
        errorType: 'CognitoAuthError',
        errorMessage: (error as Error).message
      })
    )
  } finally {
    dispatch(setIsLoading(false))
  }
}

/**
 * Component that handles user authentication state on mount
 * @param options - The component props
 * @returns Always returns null
 */
export const FetchUser = ({publicUrl}: FetchUserProps) => {
  const dispatch = useDispatch()
  const {
    auth: {showUserWidget}
  } = useBrandCapabilities()

  useEffect(() => {
    if (!showUserWidget) {
      dispatch(setIsLoading(false))
      return
    }
    updateUser({publicUrl, dispatch})

    createBroadcastChannel('auth', message => {
      if (message === AuthBroadcastChannelMessages.AuthStateChanged)
        updateUser({publicUrl, dispatch, skipLoadingState: true})
    })
  }, [showUserWidget, dispatch, publicUrl])

  useEffect(() => {
    const shouldFire =
      safelyGetStorageItem(
        sessionStorage,
        AUTHENTICATION_SUCCESS_STORAGE_KEY
      ) === '1' &&
      typeof window !== 'undefined' &&
      !isCognitoRoute(window.location.pathname)

    if (shouldFire) {
      sessionStorage.removeItem(AUTHENTICATION_SUCCESS_STORAGE_KEY)
      dispatch(userHasJustAuthenticated())
    }
  }, [dispatch])

  return null
}
